import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Enqueue Rebate Session Step
 *
 * Creates a RebateCsvTransformSession from a classified document and
 * starts the rebate CSV transform workflow. This bridges the email
 * ingestion pipeline to the existing rebate processing workflow.
 */
export const enqueueRebateSessionStep = defineStepConfig({
  type: "enqueue_rebate_session",
  name: "Enqueue Rebate Session",
  description:
    "Create a rebate CSV transform session and start the processing workflow",
  category: "Data Access",
  libraryModule: "Rebates",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The classified document (CSV or sheet) to process as a rebate",
    ),
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL)
      .optional()
      .describe("The originating inbound email for traceability"),
  }),

  outputSchema: z.object({
    rebateSessionId: z
      .string()
      .uuid()
      .describe("ID of the created RebateCsvTransformSession"),
  }),

  configSchema: z.object({
    instructions: z
      .string()
      .optional()
      .describe("Optional instructions for the rebate CSV transform"),
    autoRun: z
      .boolean()
      .optional()
      .default(true)
      .describe("Whether to start the rebate workflow immediately"),
    initialStatus: z
      .enum([
        "pending_sheet_selection",
        "queued",
        "running",
        "waiting_manual",
        "ready_for_review",
        "completed",
        "failed",
        "cancelled",
      ])
      .optional()
      .default("queued")
      .describe("Initial status for the created session"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "1 minute",
    retry: {
      maximumAttempts: 2,
    },
  },
});
